import type { ConnectedPosition } from '@angular/cdk/overlay';
import type { TemplateRef } from '@angular/core';
import {
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { PropertyPath } from 'lodash';
import { castArray, chain, compact, get, isEqual, isFunction } from 'lodash';
import type { Observable } from 'rxjs';
import {
  BehaviorSubject,
  debounce,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  Subject,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';
import { ONYX_DROPDOWN_LIMIT } from '../components/dropdown/constants';
import type { OnyxPaths } from '../components/table/interfaces';
import { ONYX_INPUT_DEBOUNCE_TIME } from '../constants';
import type { OnyxOverlayPosition } from '../enums';
import { DropdownHelper, UtilityHelper } from '../helpers';
import {
  ONYX_DROPDOWN_CONFIG,
  OnyxDropdownOverlayComponent,
} from '../internal/components/onyx-dropdown-overlay/onyx-dropdown-overlay.component';
import { OnyxDropdownOverlayService } from '../internal/services/onyx-dropdown-overlay.service';
import { OnyxFilterPipe } from '../pipes/onyx-filter.pipe';
import type {
  OnyxDropdownConfig,
  OnyxDropdownOptions,
  OnyxDropdownOptionsInput,
  OnyxDropdownOptionsSource,
  OnyxDropdownValue,
  OnyxOption,
  OnyxOptionsGroup,
} from './interfaces';

type Options<OptionValue, Value> =
  | OnyxOptionsGroup<OptionValue>[]
  | OnyxDropdownOptionsSource<OptionValue, Value>
  | null;

@Directive({
  selector: '[onyxDropdownOptions]',
  host: {
    '(click)': `!onyxDropdownDisabledOpen() ? toggle() : null`,
    '(keydown.space)': `show($event)`,
    '(keydown.enter)': `show($event)`,
    '(keydown.shift.tab)': `close()`,
    '(keydown.tab)': `close()`,
    '(keydown.arrowdown)': `showAndFocus($event)`,
  },
})
export class OnyxDropdownDirective<OptionValue, Value> {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly dropdownOverlayService = inject(OnyxDropdownOverlayService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly filterPipe = inject(OnyxFilterPipe);

  public readonly onyxDropdownOptions = input.required({
    transform: (
      options: OnyxDropdownOptionsInput<OptionValue, Value>,
    ): Options<OptionValue, Value> => {
      if (options == null) return null;
      if (DropdownHelper.isSource(options)) return options;
      return this.mapToGroups(options);
    },
  });
  public readonly onyxDropdownValue = model<OnyxDropdownValue<Value>>();
  public readonly onyxDropdownCompareKey = input<
    OnyxPaths<OptionValue> | PropertyPath
  >();
  public readonly onyxDropdownOptional = input(false);
  public readonly onyxDropdownMultiple = input(false);
  public readonly onyxDropdownShowSelectAll = input(true);
  public readonly onyxDropdownDisabled = input(false);
  public readonly onyxDropdownDisabledOpen = input(false);
  public readonly onyxDropdownAttached = model(false);
  public readonly onyxDropdownSearch = input(false);
  public readonly onyxDropdownSearchPlaceholder = input<string>();
  public readonly onyxDropdownSearchKeys =
    input<(OnyxPaths<OnyxOption<OptionValue>> | PropertyPath)[]>();
  public readonly onyxDropdownFilter = input(true);
  public readonly onyxDropdownQuery = model('');
  public readonly onyxDropdownLimit = input(ONYX_DROPDOWN_LIMIT);
  public readonly onyxDropdownPage = model(1);
  public readonly onyxDropdownTotalItems = input<number | null>(null);
  public readonly onyxDropdownNotFoundMessage = input<string>();
  public readonly onyxDropdownShowAddOption = input<boolean | 'value'>(false);
  public readonly onyxDropdownAddOptionMessage = input<string>();
  public readonly onyxDropdownAddOptionDisabled = input(false);
  public readonly onyxDropdownAddOptionShowQuery = input(true);
  public readonly onyxDropdownAddOptionTooltip = input<string>();
  public readonly onyxDropdownTemplateRef = input<TemplateRef<unknown>>();
  public readonly onyxDropdownSubheading = input<string>();
  public readonly onyxDropdownSubheadingTemplateRef = input<TemplateRef<any>>();

  public readonly onyxDropdownLoading = input(false);
  public readonly onyxDropdownEmptyTemplateRef = input<TemplateRef<any>>();
  public readonly onyxDropdownOptionTemplateRef = input<TemplateRef<any>>();
  public readonly onyxDropdownOptionSize =
    input<OnyxDropdownConfig<never>['optionSize']>(40);
  public readonly onyxDropdownTheme =
    input<OnyxDropdownConfig<never>['theme']>('light');
  public readonly onyxDropdownWidth =
    input<OnyxDropdownConfig<never>['width']>('flex');
  public readonly onyxDropdownMaxHeight =
    input<OnyxDropdownConfig<never>['maxHeight']>('360px');
  public readonly onyxDropdownPositions = input<OnyxOverlayPosition[]>();
  public readonly onyxDropdownOverridePositions =
    input<Partial<ConnectedPosition>>();
  public readonly onyxDropdownTargetRef = input<HTMLElement>();

  public readonly onyxDropdownOptionsChange =
    output<OnyxDropdownOptions<OptionValue>>();
  public readonly onyxDropdownTotalItemsChange = output<number>();
  public readonly onyxDropdownFetchingChange = output<boolean>();
  public readonly onyxDropdownSelectedValueChange = output<{
    value: Value | null;
    isManual: boolean;
  }>();
  public readonly onyxDropdownSelectedValuesChange = output<{
    values: Value[];
    isManual: boolean;
  }>();
  public readonly onyxDropdownSelectedOptionChange = output<{
    option: OnyxOption<OptionValue> | null;
    isManual: boolean;
  }>();
  public readonly onyxDropdownSelectedOptionsChange = output<{
    options: OnyxOption<OptionValue>[];
    isManual: boolean;
  }>();
  public readonly onyxDropdownSelectedOptionValueChange = output<{
    value: OptionValue | null;
    isManual: boolean;
  }>();
  public readonly onyxDropdownSelectedOptionValuesChange = output<{
    values: OptionValue[];
    isManual: boolean;
  }>();
  public readonly onyxDropdownAddOption = output<string>();
  public readonly onyxDropdownFocus = output();

  private attached = signal(false);
  private searching = signal(false);

  private selectedOptionsUpdate$ = new Subject<{
    options: Options<OptionValue, Value>;
    values: Value[];
    isInitial: boolean;
  }>();
  private filteredOptionsUpdate$ = new Subject<{
    source: OnyxDropdownOptionsSource<OptionValue, Value>;
    query: string;
    page: number;
  }>();
  private filteredOptions$ = new BehaviorSubject<
    OnyxOptionsGroup<OptionValue>[] | null
  >(null);

  private values = computed(() =>
    castArray(this.onyxDropdownValue()).filter(
      (value): value is Value => value != null && !isFunction(value),
    ),
  );
  private selectedOptions = signal<OnyxOption<OptionValue>[]>([]);
  private selectedValues = computed(() =>
    this.selectedOptions()
      .map((option) => this.getValue(option))
      .filter((value) => !isFunction(value)),
  );
  private selectedCompareValues = computed(
    () =>
      new Set(
        this.selectedOptions().map((option) =>
          this.getCompareValue(option.value),
        ),
      ),
  );

  constructor() {
    effect(() => {
      const attached = this.attached();
      untracked(() => this.onyxDropdownAttached.set(attached));
    });

    effect(() =>
      this.onyxDropdownAttached()
        ? this.attachDropdown()
        : this.detachDropdown(),
    );

    this.selectedOptionsUpdate$
      .pipe(
        filter(
          ({ isInitial, values }) =>
            isInitial ||
            !isEqual(this.selectedValues().toSorted(), values.toSorted()),
        ),
        switchMap(({ options, values }) => {
          this.onyxDropdownFetchingChange.emit(true);

          return this.getSelectedOptions(options, values).pipe(
            finalize(() => this.onyxDropdownFetchingChange.emit(false)),
          );
        }),
        map((options) =>
          compact(options).map((option): OnyxOption<OptionValue> => ({
            ...option,
            _selected: true,
            _hidden: true,
          })),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((options) => this.emitChanges(options, false));

    this.filteredOptionsUpdate$
      .pipe(
        map((data) => {
          const query = UtilityHelper.toCompareString(data.query);
          return { ...data, query: query.length < 3 ? '' : query };
        }),
        distinctUntilChanged(
          (previous, current) =>
            previous.query === current.query && previous.page === current.page,
        ),
        tap(() => this.searching.set(true)),
        debounce(({ query }) =>
          query ? timer(ONYX_INPUT_DEBOUNCE_TIME) : of(0),
        ),
        switchMap(({ source, query, page }) =>
          UtilityHelper.maybeAsyncToObservable(
            source.list(query, { page, limit: this.onyxDropdownLimit() }),
          ).pipe(
            map((result) => ({ query, page, ...result })),
            tap({
              next: () => this.searching.set(false),
              error: () => this.searching.set(false),
            }),
          ),
        ),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ query, page, options, totalItems }) => {
        const existingGroups =
          page === 1 ? [] : (this.filteredOptions$.value ?? []);
        const groups = this.mapToGroups(options) ?? [];

        const mergedGroups = chain(existingGroups)
          .map((group): OnyxOptionsGroup<OptionValue> => ({
            ...group,
            options: group.options.map((option) => ({
              ...option,
              _hidden: !this.filterPipe.transform([option], query, [
                'name',
                'description',
              ]).length,
              _selected: this.selectedCompareValues().has(
                this.getCompareValue(option.value),
              ),
            })),
          }))
          .concat(groups)
          .groupBy((group) => group.id)
          .map((groups) => ({
            ...groups[0],
            options: chain(groups)
              .flatMap((group) => group.options)
              .uniqBy((option) => this.getCompareValue(option.value))
              .value(),
          }))
          .orderBy((group) => group.id)
          .value();

        this.filteredOptions$.next(mergedGroups);
        this.onyxDropdownOptionsChange.emit(options);
        this.onyxDropdownTotalItemsChange.emit(totalItems);
      });

    let isInitialSelectedOptionsUpdate = true;
    effect(() => {
      const options = this.onyxDropdownOptions();
      const values = this.values();
      if (options == null) return;

      untracked(() =>
        this.selectedOptionsUpdate$.next({
          options,
          values,
          isInitial: isInitialSelectedOptionsUpdate,
        }),
      );
      isInitialSelectedOptionsUpdate = false;
    });

    let lastQuery = this.onyxDropdownQuery();
    effect(() => {
      const query = this.onyxDropdownQuery();
      const page = this.onyxDropdownPage();

      if (query !== lastQuery) {
        untracked(() => this.onyxDropdownPage.set(1));
      }
      lastQuery = query;

      const options = this.onyxDropdownOptions();
      if (DropdownHelper.isSource(options)) {
        this.filteredOptionsUpdate$.next({ source: options, query, page });
      } else {
        this.filteredOptions$.next(this.filterOptions(options, query));
        this.onyxDropdownOptionsChange.emit(options);
        this.onyxDropdownTotalItemsChange.emit(
          DropdownHelper.mapToOptions(options)?.length ?? 0,
        );
      }
    });
  }

  protected toggle(): void {
    if (this.onyxDropdownDisabled()) return;

    if (this.attached()) {
      this.detachDropdown();
    } else {
      this.show();
    }
  }

  protected show(event?: Event): void {
    if (this.attached() || this.onyxDropdownDisabled()) return;

    this.attachDropdown();
    if (this.onyxDropdownSearch()) setTimeout(() => this.focusDropdown());

    if (this.onyxDropdownSearch() || this.onyxDropdownQuery() === undefined) {
      event?.preventDefault();
    }
  }

  protected close(): void {
    this.detachDropdown();
  }

  protected showAndFocus(event: Event): void {
    if (this.onyxDropdownDisabled()) return;

    this.attachDropdown();
    setTimeout(() => this.focusDropdown());

    event.preventDefault();
  }

  private attachDropdown(): void {
    if (this.attached() || this.onyxDropdownDisabled()) return;

    if (this.onyxDropdownSearch()) this.onyxDropdownQuery.set('');

    this.dropdownOverlayService.attachDropdown(
      this.elementRef,
      this.viewContainerRef,
      OnyxDropdownOverlayComponent,
      ONYX_DROPDOWN_CONFIG,
      {
        options: this.filteredOptions$,
        templateRef: this.onyxDropdownTemplateRef(),
        values: this.values(),
        compareKey: this.getCompareKey(),
        optional: this.onyxDropdownOptional(),
        multiple: this.onyxDropdownMultiple(),
        showSelectAll: this.onyxDropdownShowSelectAll(),
        search: DropdownHelper.isSource(this.onyxDropdownOptions())
          ? true
          : this.onyxDropdownSearch(),
        searchPlaceholder: this.onyxDropdownSearchPlaceholder(),
        limit: this.onyxDropdownLimit(),
        query: this.onyxDropdownQuery,
        page: this.onyxDropdownPage,
        totalItems: this.onyxDropdownTotalItems,
        notFoundMessage: this.onyxDropdownNotFoundMessage(),
        loading: this.onyxDropdownLoading,
        searching: this.searching,
        showAddOption: this.onyxDropdownShowAddOption(),
        addOptionMessage: this.onyxDropdownAddOptionMessage(),
        addOptionDisabled: this.onyxDropdownAddOptionDisabled(),
        addOptionShowQuery: this.onyxDropdownAddOptionShowQuery(),
        addOptionTooltip: this.onyxDropdownAddOptionTooltip(),
        subheading: this.onyxDropdownSubheading(),
        subheadingTemplateRef: this.onyxDropdownSubheadingTemplateRef(),
        emptyTemplateRef: this.onyxDropdownEmptyTemplateRef(),
        optionTemplateRef: this.onyxDropdownOptionTemplateRef(),
        maxHeight: this.onyxDropdownMaxHeight(),
        theme: this.onyxDropdownTheme(),
        width: this.onyxDropdownWidth(),
        positions: this.onyxDropdownPositions(),
        overridePositions: this.onyxDropdownOverridePositions(),
        elementRef: this.elementRef.nativeElement,
        targetRef: this.onyxDropdownTargetRef(),
        optionSize: this.onyxDropdownOptionSize(),

        focusElement: () => this.focusElement(),
        selectOptions: (options) => this.emitChanges(options, true),
        addOption: (query) => this.onyxDropdownAddOption.emit(query),
        close: () => this.detachDropdown(),
      },
    );

    this.attached.set(true);
    this.dropdownOverlayService.detach$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.attached.set(false));
  }

  private detachDropdown(): void {
    if (!this.attached()) return;

    this.dropdownOverlayService.detachDropdown();
    this.focusElement();
    this.attached.set(false);
  }

  private focusElement(): void {
    if (this.onyxDropdownFocus['listeners'] != null) {
      this.onyxDropdownFocus.emit();
    } else {
      this.elementRef.nativeElement.focus();
    }
  }

  private focusDropdown(): void {
    const dropdown = this.dropdownOverlayService.componentRef()?.instance as
      OnyxDropdownOverlayComponent<OptionValue> | undefined;
    dropdown?.focus();
  }

  private emitChanges(
    options: OnyxOption<OptionValue>[],
    isManual: boolean,
  ): void {
    this.selectedOptions.set(options);
    this.onyxDropdownSelectedOptionChange.emit({
      option: options.at(0) ?? null,
      isManual,
    });
    this.onyxDropdownSelectedOptionsChange.emit({
      options,
      isManual,
    });
    this.onyxDropdownSelectedOptionValueChange.emit({
      value: options.at(0)?.value ?? null,
      isManual,
    });
    this.onyxDropdownSelectedOptionValuesChange.emit({
      values: options.map((option) => option.value),
      isManual,
    });

    if (!isManual) return;

    const values = options.map((option) => this.getValue(option));
    this.onyxDropdownValue.set(
      this.onyxDropdownMultiple() ? values : (values.at(0) ?? null),
    );
    this.onyxDropdownSelectedValueChange.emit({
      value: values.at(0) ?? null,
      isManual,
    });
    this.onyxDropdownSelectedValuesChange.emit({
      values,
      isManual,
    });
  }

  private getSelectedOptions(
    options: Options<OptionValue, Value>,
    values: Value[],
  ): Observable<(OnyxOption<OptionValue> | null)[]> {
    if (!values.length) return of([]);

    if (!DropdownHelper.isSource(options)) {
      const compareValues = new Set(
        values.map((value) => this.getCompareValue(value)),
      );
      return of(
        DropdownHelper.mapToOptions(options)!.filter((option) =>
          compareValues.has(this.getCompareValue(option.value)),
        ),
      );
    }

    return forkJoin(
      values.map((value) =>
        UtilityHelper.maybeAsyncToObservable(options.get(value)),
      ),
    );
  }

  private getCompareKey(): OnyxDropdownConfig<OptionValue>['compareKey'] {
    const options = this.onyxDropdownOptions();
    return DropdownHelper.isSource(options)
      ? options.idKey
      : this.onyxDropdownCompareKey();
  }

  private getCompareValue(value: OptionValue | Value): unknown {
    return DropdownHelper.getCompareValue(value, this.getCompareKey());
  }

  private getValue(option: OnyxOption<OptionValue>): Value {
    const options = this.onyxDropdownOptions();
    return DropdownHelper.isSource(options) && options.idKey
      ? get(option.value, options.idKey)
      : (option.value as unknown as Value);
  }

  private filterOptions(
    groups: OnyxOptionsGroup<OptionValue>[] | null,
    query: string,
  ): OnyxOptionsGroup<OptionValue>[] | null {
    if (groups == null) return null;
    if (!this.onyxDropdownFilter()) return groups;

    return groups.map((group) => ({
      ...group,
      options: this.filterPipe.transform(group.options, query, [
        'name',
        'description',
        ...((this.onyxDropdownSearchKeys() ?? []) as OnyxPaths<
          OnyxOption<OptionValue>
        >[]),
      ]),
    }));
  }

  private mapToGroups(
    options: OnyxDropdownOptions<OptionValue>,
  ): OnyxOptionsGroup<OptionValue>[] | null {
    return DropdownHelper.mapToGroups(
      options,
      this.onyxDropdownSubheading(),
      this.onyxDropdownSubheadingTemplateRef(),
    );
  }
}
